// @flow
import { fromJS, OrderedMap } from "immutable"
import { createSelector } from "reselect"
import type { FetchedCurrencies } from "@kiwicom/nitro/lib/records/Currency"
import type { InputSelector } from "reselect"

import type { LanguageInfo } from "../../records/LanguageInfo"
import type { Phone, Phones } from "../../records/Phone"
import type { StringMap } from "../../records/StringMap"
import type { Continents } from "../../records/Continent"
import type { Airlines } from "../../records/Airline"

export type StaticDataState = {|
  +continents: Continents,
  +countries: StringMap,
  +countryCurrencies: StringMap,
  +countryPhonePrefixes: StringMap,
  +airlineTerms: StringMap,
  +currencies: FetchedCurrencies,
  +languageInfo: LanguageInfo,
  +languageCountries: StringMap,
  +languageFlags: StringMap,
  +languageNames: StringMap,
  +phones: Phones,
  +phonesOrder: string[],
  +airlines: Airlines,
|}

const initialState = {
  airlineTerms: {},
  countries: {},
  continents: {},
  countryCurrencies: {},
  countryPhonePrefixes: {},
  currencies: {},
  languageInfo: {
    // 'null' would require too much refactoring
    id: "en",
    name: "English",
    displayName: "English",
    phone: "gb",
    email: "en",
    defaultCountry: "gb",
    api: "en",
    moment: "en-gb",
    jumio: "en_GB",
    nginx: "en",
    countriesTranslations: "EN",
    translations: "en",
    phraseApp: "en-GB",
    canonical: "",
    hreflang: "en-GB",
    iso: "en-GB",
    locations: "en",
    dateFormat: "ddd D MMM",
    dateFormatShort: "D/M",
    dateFormatLong: "DD.MM.YYYY",
    dateFormatPlain: "L",
    timeFormat: "LT",
    durationFormat: "H[h] mm[m]",
    durationFormatShort: "m[m] ss[s]",
    dimension: "__x__ cm",
    weight: "__x__ kg",
    latinInputs: false,
    currency: "gbp",
    direction: "ltr",
    flag: "gb",
    fontSubsets: "greek,latin-ext",
    firstNamePlaceholder: "Harry James",
    lastNamePlaceholder: "Brown",
    addressPlaceholder: "71 Wall Stt",
    cityPlaceholder: "New York",
    zipCodePlaceholder: "10005",
    idNumberPlaceholder: "1234567890",
    companyVatPlaceholder: "10007",
    companyNamePlaceholder: "Kiwi.com",
    distanceUnit: "__x__ km",
    distanceUnitConversionRate: "1",
    elevioLang: "",
    specialFont: "",
    decimalSeparator: ".",
    thousandsSeparator: ",",
    separateFourDigits: "1",
  },
  languageCountries: {},
  languageFlags: {},
  languageNames: {},
  phones: {},
  phonesOrder: [],
  airlines: {},
}

function staticDataReducer(state: StaticDataState = initialState) {
  return state
}

export default staticDataReducer

type State = { +staticData: StaticDataState }

const getState = state => state.staticData

export const getContinents: InputSelector<State, any, Continents> = createSelector(
  getState,
  data => data.continents,
)

export const getCountries: InputSelector<State, any, StringMap> = createSelector(
  getState,
  data => data.countries,
)

export const getCountriesMap: InputSelector<State, any, OrderedMap> = createSelector(
  getCountries,
  data => OrderedMap(fromJS(data)).sort(),
)

export const getCountryCurrencies: InputSelector<State, any, StringMap> = createSelector(
  getState,
  data => data.countryCurrencies,
)

export const getLanguageInfo: InputSelector<State, any, LanguageInfo> = createSelector(
  getState,
  data => data.languageInfo,
)

export const getCountryPhonePrefixes: InputSelector<State, any, StringMap> = createSelector(
  getState,
  data => data.countryPhonePrefixes,
)

export const getLanguageCountries: InputSelector<State, any, StringMap> = createSelector(
  getState,
  data => data.languageCountries,
)

export const getLanguageFlags: InputSelector<State, any, StringMap> = createSelector(
  getState,
  data => data.languageFlags,
)

export const getLanguageNames: InputSelector<State, any, StringMap> = createSelector(
  getState,
  data => data.languageNames,
)

export const getAirlineTerms: InputSelector<State, any, StringMap> = createSelector(
  getState,
  data => data.airlineTerms,
)

export const getCurrencies: InputSelector<State, any, FetchedCurrencies> = createSelector(
  getState,
  data => data.currencies,
)

export const getSortedPhones: InputSelector<State, any, Phone[]> = createSelector(
  getState,
  data => data.phonesOrder.map(id => data.phones[id]),
)

export const getAirlines: InputSelector<State, any, Airlines> = createSelector(
  getState,
  data => data.airlines,
)
